import { useFetchProjects } from '@internal/plugin-jira-issues';
import SlackIcon from '@mui/icons-material/Chat';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import LinkIcon from '@mui/icons-material/Link';
import React, { useState } from 'react';

import { useEntity } from '@backstage/plugin-catalog-react';

import {
  Box,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';

import { EntityUpdateDialog } from '../../EntityUpdateDialog';
import { JiraIssuesLogo } from './JiraIssuesLogo';
import { SelectionOption, GroupAnnotationInfo } from './types';

export const GroupManageAnnotationsActions = () => {
  const [annotationsDialogOpen, setAnnotationsDialogOpen] = useState(false);
  const [selectedAnnotation, setSelectedAnnotation] = useState('');
  const [selectedAnnotationHint, setSelectedAnnotationHint] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { entity } = useEntity();
  const { projects, loading: jiraLoading } = useFetchProjects();

  const getSlackChannelOptions = (): SelectionOption[] => {
    const communications =
      (entity.spec?.communications as Array<{
        type: string;
        channel: string;
        title: string;
      }>) ?? [];

    return communications
      .filter(comm => comm.type === 'slack')
      .map(comm => ({
        id: `${comm.channel}|||${comm.title}`,
        title: `${comm.channel} - ${comm.title}`,
      }));
  };

  const annotations: GroupAnnotationInfo[] = [
    {
      name: 'jira/project-key',
      title: 'Jira project keys',
      icon: <JiraIssuesLogo />,
      hint: 'Add your Jira project keys for team issue tracking.',
      options: projects.map(project => ({
        id: project.key,
        title: `${project.name} (${project.key})`,
      })),
      multiple: true,
    },
    {
      name: 'confluence/wiki-urls',
      title: 'Confluence Wiki URLs',
      icon: <CloudQueueIcon fontSize="small" />,
      hint: 'Add your Confluence Wiki URLs for team documentation.',
      options: [],
      multiple: true,
    },
    {
      name: 'slack',
      title: 'Slack channels',
      icon: <SlackIcon fontSize="small" />,
      hint: 'Add your Slack channels for team communication.',
      options: getSlackChannelOptions(),
      multiple: true,
    },
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAnnotationClick = (annotation: string, hint: string) => {
    setSelectedAnnotation(annotation);
    setSelectedAnnotationHint(hint);
    setAnnotationsDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseAnnotationsDialog = () => {
    setAnnotationsDialogOpen(false);
  };

  const getOptionsForAnnotation = (annotationName: string) => {
    const annotation = annotations.find(a => a.name === annotationName);
    return annotation?.options ?? [];
  };

  const isAnnotationLoading = (annotationName: string) => {
    if (annotationName === 'jira/project-key') {
      return jiraLoading;
    }
    return false;
  };

  return (
    <Box>
      <Tooltip title="Manage team Links">
        <Button
          aria-controls="manage-links-menu"
          aria-haspopup="true"
          onClick={handleMenuOpen}
          startIcon={<LinkIcon />}
        >
          Manage Links
        </Button>
      </Tooltip>

      <Menu
        id="manage-links-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {annotations.map(annotation => (
          <MenuItem
            key={annotation.name}
            onClick={() =>
              handleAnnotationClick(annotation.name, annotation.hint ?? '')
            }
          >
            <ListItemIcon>{annotation.icon}</ListItemIcon>
            <ListItemText primary={annotation.title} />
          </MenuItem>
        ))}
      </Menu>

      <EntityUpdateDialog
        key={selectedAnnotation}
        dialogTitle={
          selectedAnnotation
            ? `Manage ${
                annotations.find(a => a.name === selectedAnnotation)?.title
              } for your team`
            : 'Manage your team Links'
        }
        open={annotationsDialogOpen}
        onClose={handleCloseAnnotationsDialog}
        property={{
          propertyType:
            selectedAnnotation === 'slack'
              ? 'COMMUNICATIONS_SLACK'
              : 'ANNOTATION',
          propertyValue: selectedAnnotation,
          propertyHint: selectedAnnotationHint,
        }}
        propertyOptions={getOptionsForAnnotation(selectedAnnotation)}
        loading={isAnnotationLoading(selectedAnnotation)}
      />
    </Box>
  );
};
